import React, { useState, useMemo, useEffect } from 'react';
import { Box } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import InputField from '../formItem/inputField';
import Button from '../button';
import StyledContainer from './components/styledContainer';
import NavigationBar from '../navigation';
import FormLabel from '../formItem/formLabel';
import Dropdown from '../dropdown';
import { membershipAPI, userAPI } from '../../actions/imsServer';
import Popup from '../../components/popup';

export const OrganizationSearchDetail = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const selectedOrganization = location.state?.selectedOrganization;
    const source = location.state?.source; // 'signup' or 'main'
    const [division, setDivision] = useState('');
    const [section, setSection] = useState('');
    const [popupOpen, setPopupOpen] = useState(false);
    const [, setIsLoading] = useState(false);
    const [popupMessage, setPopupMessage] = useState('');
    const [popupSubMessage, setPopupSubMessage] = useState('');

    // divisionSections에서 세부소속1 옵션 생성
    const divisionOptions = useMemo(() => {
        if (!selectedOrganization?.divisionSections) return [];


        console.log(selectedOrganization.divisionSections)
        const divisionSections = typeof selectedOrganization.divisionSections === 'string'
            ? JSON.parse(selectedOrganization.divisionSections)
            : selectedOrganization.divisionSections;

        return Object.keys(divisionSections).map(key => ({
            value: key,
            label: key
        }));
    }, [selectedOrganization?.divisionSections]);

    // 선택된 세부소속1에 따른 세부소속2 옵션 생성
    const sectionOptions = useMemo(() => {
        if (!division || !selectedOrganization?.divisionSections) return [];

        const divisionSections = typeof selectedOrganization.divisionSections === 'string'
            ? JSON.parse(selectedOrganization.divisionSections)
            : selectedOrganization.divisionSections;

        const sectionData = divisionSections[division];
        if (!Array.isArray(sectionData)) return [];

        return sectionData.map(item => ({
            value: item,
            label: item
        }));
    }, [division, selectedOrganization?.divisionSections]);

    // 세부소속1 옵션이 하나뿐이면 자동 선택
    useEffect(() => {
        if (divisionOptions.length === 1 && !division) {
            setDivision(divisionOptions[0].value);
        }
    }, [divisionOptions, division]);

    // 세부소속2 옵션이 하나뿐이면 자동 선택 (세부소속1이 선택된 경우만)
    useEffect(() => {
        if (sectionOptions.length === 1 && sectionOptions.length > 0 && !section && division) {
            setSection(sectionOptions[0].value);
        }
    }, [sectionOptions, section, division]);

    const handleDivisionChange = (option) => {
        setDivision(option.value);
        setSection(''); // 세부소속1이 변경되면 세부소속2 초기화
    };

    const handleSectionChange = (option) => {
        setSection(option.value);
    };

    // 팝업 표시 함수
    const showPopup = (message, subMessage = '') => {
        setPopupMessage(message);
        setPopupSubMessage(subMessage);
        setPopupOpen(true);
    };

    // 팝업 닫기 함수
    const handleClosePopup = () => {
        setPopupOpen(false);
    };

    const handleConfirmSelection = async () => {
        if (!selectedOrganization) return;

        setIsLoading(true);

        try {
            if (source === 'main' || source === 'membership') {
                // main에서 온 경우: 3가지 API를 순차적으로 호출
                const meInfo = JSON.parse(localStorage.getItem('meInfo') || '{}');

                const membershipsResponse = await membershipAPI.getMemberships({
                    userId: meInfo.id,
                    type: 1,
                    state: 0,
                    expand: 'organization',
                });

                // 중복 체크: 같은 기관, 같은 division, 같은 section에 이미 가입했는지 확인
                const isDuplicate = membershipsResponse.data.memberships.some(membership => {
                    const sameOrg = membership.organizationId === selectedOrganization.id;
                    const sameDivision = (membership.division || null) === (division || null);
                    const sameSection = (membership.section || null) === (section || null);
                    return sameOrg && sameDivision && sameSection;
                });

                if (isDuplicate) {
                    showPopup(t('already_joined_this_organization'), t('you_cannot_join_the_same_division_section_of_the_same_organization_more_than_once'));
                    setIsLoading(false);
                    return;
                }

                const membershipData = {
                    userId: meInfo.id,
                    organizationId: selectedOrganization.id,
                    division: division || null,
                    section: section || null,
                    type: 1, // 일반 멤버
                    state: 0, // 정상
                    info: '',
                };
                await membershipAPI.registerMembership(membershipData);

                if (membershipsResponse.data.total === 0) {
                    await userAPI.updateUser({
                        id: meInfo.id,
                        mainOrganizationId: selectedOrganization.id
                    });

                    const meInfoResponse = await userAPI.getMyInfo();
                    if (meInfoResponse?.data?.user) {
                        localStorage.setItem('meInfo', JSON.stringify(meInfoResponse.data.user));
                    }
                }

                if (source === 'main') {
                    navigate('/');
                } else {
                    // membership으로 돌아갈 때 기관 검색/등록 화면을 히스토리에서 제거                   
                    // 현재 히스토리: [마이페이지, membership, organization-search, organization-search-detail]
                    // navigate(-2)로 두 단계를 건너뛰어 membership으로 이동
                    // 이렇게 하면 organization-search와 organization-search-detail이 모두 히스토리에서 제거됨
                    navigate(-2);
                }
            } else {
                // signup에서 온 경우: signup으로 돌아가기 (이전 formData 유지)
                const previousFormData = location.state?.formData;

                console.log("previousFormData", previousFormData);
                navigate('/auth/signup', {
                    state: {
                        selectedOrganization: selectedOrganization,
                        selectedDivision: division,
                        selectedSection: section,
                        step: 1,
                        formData: previousFormData ? {
                            ...previousFormData,
                            selectedOrganization: selectedOrganization,
                            division: division,
                            section: section,
                            organizationId: selectedOrganization?.id
                        } : undefined
                    },
                });
            }
        } catch (error) {
            console.error('Failed to save organization:', error);
            // TODO: 에러 메시지 표시
        } finally {
            setIsLoading(false);
        }
    };

    // 저장 버튼 활성화 조건
    const isSaveButtonEnabled = selectedOrganization &&
        (divisionOptions.length === 0 || division) && // 세부소속1이 없거나 선택된 경우
        (sectionOptions.length === 0 || section); // 세부소속2가 없거나 선택된 경우

    const handleClearOrganizationSearch = () => {
        navigate(-1);
    };

    return (
        <StyledContainer showVersion={false}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '40px',
                flex: '1 0 0',
                alignSelf: 'stretch',
            }}>
                <NavigationBar showLeftIcon={true} showRightIcon={false} />
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',

                    width: '100%',
                }}>
                    {/* Selected Organization Display */}
                    {selectedOrganization && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px' }}>
                            <FormControl
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: '10px',
                                    alignSelf: 'stretch',
                                    flex: 1,
                                }}
                            >
                                <FormLabel text={t('affiliated_organization')} required sx={{ width: '64px', textAlign: 'left' }} />
                                <InputField
                                    required
                                    disabled
                                    value={selectedOrganization.name}
                                    id="name"
                                    name="name"
                                    onClear={handleClearOrganizationSearch}
                                    sx={{ flex: 1 }}
                                />
                            </FormControl>
                            {divisionOptions.length > 0 && (
                                <FormControl
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        gap: '10px',
                                        alignSelf: 'stretch',
                                        flex: 1,
                                    }}
                                >
                                    <FormLabel text={t('division_1')} required={divisionOptions.length > 0} sx={{ width: '64px', textAlign: 'left' }} />
                                    <Dropdown
                                        required={divisionOptions.length > 0}
                                        options={divisionOptions}
                                        value={division}
                                        onChange={handleDivisionChange}
                                        placeholder={t('please_select_division_1')}
                                        sx={{ flex: 1 }}
                                    />
                                </FormControl>
                            )}
                            {/* 세부소속2가 있을 때만 표시 */}
                            {sectionOptions.length > 0 && (
                                <FormControl
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        gap: '10px',
                                        alignSelf: 'stretch',
                                        flex: 1,
                                    }}
                                >
                                    <FormLabel text={t('division_2')} required sx={{ width: '64px', textAlign: 'left' }} />
                                    <Dropdown
                                        required
                                        options={sectionOptions}
                                        value={section}
                                        onChange={handleSectionChange}
                                        placeholder={t('please_select_division_2')}
                                        disabled={!division}
                                        sx={{ flex: 1 }}
                                    />
                                </FormControl>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
            {/* Confirm Button */}
            {
                selectedOrganization && (
                    <Box sx={{
                        display: 'flex',
                        width: '100%',
                        flexDirection: 'row',
                        justifyContent: 'flex-end',
                        gap: '10px',
                        padding: '20px 0px',
                    }}>
                        <Button
                            variant="gray"
                            size="large"
                            onClick={handleClearOrganizationSearch}
                            sx={{ flex: 1 }}

                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            variant="normal"
                            size="large"
                            onClick={handleConfirmSelection}
                            disabled={!isSaveButtonEnabled}
                            sx={{ flex: 1 }}
                        >
                            {t('save')}
                        </Button>
                    </Box>
                )
            }

            <Popup
                open={popupOpen}
                message={popupMessage}
                subMessage={popupSubMessage}
                onConfirm={handleClosePopup}
            />
        </StyledContainer >
    );
};

export default OrganizationSearchDetail;
